import type { ImportRowError } from '@paintscout/api'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import React from 'react'
import { formatGroupLabels, groupRowErrors } from './groupRowErrors'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    border: `1px solid ${theme.paintscout.palette.semantic.Danger.S90}`,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    textAlign: 'left'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    backgroundColor: theme.paintscout.palette.semantic.Danger.S95,
    color: theme.paintscout.palette.semantic.Danger.S50
  },
  icon: {
    fontSize: 20,
    color: theme.paintscout.palette.semantic.Danger.S60
  },
  title: {
    flex: 1,
    color: 'inherit'
  },
  // A subtle link, not a button: the mock puts it in the header (ENG-2559).
  download: {
    padding: 0,
    border: 'none',
    background: 'none',
    font: 'inherit',
    color: 'inherit',
    textDecoration: 'underline',
    cursor: 'pointer'
  },
  // No scroll area: there is one line per error code, and a row can carry at most a handful.
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none'
  },
  group: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    '& + &': {
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    }
  },
  reason: {
    flexShrink: 0,
    minWidth: 140,
    fontWeight: 500
  },
  // Long labels wrap rather than truncate, so no part of the list is hidden.
  labels: {
    minWidth: 0,
    overflowWrap: 'anywhere',
    color: theme.paintscout.palette.brand.Neutral.P60
  }
}))

export interface RowErrorsPanelProps {
  title: string
  errors: ImportRowError[]
  /** Renders the header's "Download" link. */
  onDownload?: () => void
  'data-testid'?: string
}

/** Row errors grouped by reason, one line each (ENG-2559). Shared by the Review and Import steps. */
export default function RowErrorsPanel({ title, errors, onDownload, 'data-testid': testId }: RowErrorsPanelProps) {
  const classes = useStyles()
  const groups = groupRowErrors(errors)

  return (
    <div className={classes.root} data-testid={testId}>
      <div className={classes.header}>
        <ErrorOutlineIcon className={classes.icon} />
        <Typography variant="body1" className={classes.title}>
          {title}
        </Typography>
        {onDownload && (
          <button type="button" className={classes.download} onClick={onDownload}>
            Download
          </button>
        )}
      </div>
      <ul className={classes.list}>
        {groups.map((group) => (
          <li key={group.code} className={classes.group}>
            <Typography variant="body2" className={classes.reason}>
              {group.message}
            </Typography>
            <Typography variant="body2" className={classes.labels}>
              {formatGroupLabels(group.labels)}
            </Typography>
          </li>
        ))}
      </ul>
    </div>
  )
}
